import * as React from "react";
import * as React44ms from "../../../Util/form-utils/react-44ms";

import {
  CustomerInformation_Address_Response,
  PRESENTATION_SCROLL_ACTIONS,
  useCustomerCartId,
  useCustomerInformationQuery,
  useCustomerSession,
  useJoinDrawerUserExperience,
  usePresenterForScroll_asController,
  useProminentError,
} from "../../../Common";

import { ReactCheckout2022_AllTheForms } from "../../all-the-forms";
import {
  BillingAddressFormExpects,
  DeliveryDetailsForm,
  ShippingAddressFormExpects,
} from "../../model";

import {
  DataMutableSourceShippingClientStorageData_ControllerInterface,
  useDataMutableSourceShippingClientStorageData,
  useDataMutableSourceShippingClientStorageData_asController,
} from "../data-mutable-source-shipping-client-storage-data-provider";
import { useDataMutableSourceShippingSessionStorageData } from "../data-mutable-source-shipping-session-storage-data-provider";
import { getLastKnownEmail } from "../../delivery-details/email-sync-cache";
import { getLastKnownAddress } from "../../delivery-details/address-sync-cache";
import {
  DataMutationDeleteCustomerAddress_Request,
  useDataMutationDeleteCustomerAddress,
  useDataMutationDeleteCustomerAddress_asController,
} from "../data-mutation--delete-customer-address";
import { useDrawerDelivery_asController } from "../drawer-delivery-provider";
import {
  useMyBagDataSourceIntersection,
  useMyBagPresentationModel,
} from "../my-bag-provider";
import {
  useDeliveryDetailsPaymentPageKickoff,
  useDeliveryDetailsPaymentPageKickoff_asController,
} from "./delivery-details-payment-page-kickoff-provider";
import { useDeliveryDetailsFormRef } from "./delivery-details-form-ref-provider";
import { ReactCheckout2022_AddressFormTypes } from "../../delivery-details/delivery-details-address-form-types";

interface DeliveryDetailsUserExperienceConsumerInterface {
  deliveryFormPresentable: boolean;
  isLoading: boolean;
}
const defaultValue =
  {} as unknown as DeliveryDetailsUserExperienceConsumerInterface;
const DeliveryDetailsUserExperienceContext = React.createContext(defaultValue);

interface DeliveryDetailsUserExperience_ControllerInterface {
  handleUserWantsToLoginInsteadBecauseTheirEmailIsAlreadyRegistered: () => void;
  handleShowDrawerDelivery_viewItemDetailsLink: () => void;
  handleDeleteSavedAddress: (
    savedAddress: CustomerInformation_Address_Response
  ) => Promise<void>;
  handleLastSelectedSavedAddressIdChange: (
    lastSelectedSavedAddressId: Parameters<
      DataMutableSourceShippingClientStorageData_ControllerInterface["handleLastSelectedSavedAddressIdChange"]
    >[0]
  ) => void;
  handleLastSelected_isBillingAndShippingTheSameChange: (
    lastSelected_isBillingAndShippingTheSame: Parameters<
      DataMutableSourceShippingClientStorageData_ControllerInterface["handleLastSelected_isBillingAndShippingTheSameChange"]
    >[0]
  ) => void;
  handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange: (
    lastSelected_authoriseDeliveryToBeLeftWithoutSignature: Parameters<
      DataMutableSourceShippingClientStorageData_ControllerInterface["handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange"]
    >[0]
  ) => Promise<void>;
  handleReviewAndPay: () => void;
}
const controllerDefaultValue =
  {} as unknown as DeliveryDetailsUserExperience_ControllerInterface;
const DeliveryDetailsUserExperience_ControllerContext = React.createContext(
  controllerDefaultValue
);

export enum DeliveryDetailsUserExperience_ProminentErrorAreas {
  DELETE_ADDRESS = "DELETE_ADDRESS",
}

const formTreeHasFrontendError = (node: unknown): boolean => {
  if (!node) {
    return false;
  }
  if (Array.isArray(node)) {
    return node.some((entry) => formTreeHasFrontendError(entry));
  }
  if (typeof node !== "object") {
    return false;
  }

  const asRecord = node as Record<string, unknown>;
  if (
    typeof asRecord.error === "string" &&
    (asRecord.error as string).trim().length > 0
  ) {
    return true;
  }

  return Object.values(asRecord).some((entry) => formTreeHasFrontendError(entry));
};

type AddressFormPresentationType =
  | "Autocomplete"
  | "Manual"
  | "LoggedInCustomerSavedAddresses";

const getRenderedAddressFormPresentationType = (
  addressFormType: ReactCheckout2022_AddressFormTypes
): AddressFormPresentationType | null => {
  if (typeof document === "undefined") {
    return null;
  }

  const addressSection = document.querySelector(`.address.${addressFormType}`);
  if (!(addressSection instanceof HTMLElement)) {
    return null;
  }

  const presentationClass = Array.from(addressSection.classList).find((item) =>
    item.startsWith("AddressFormPresentationType-")
  );
  if (!presentationClass) {
    return null;
  }

  return presentationClass.replace(
    "AddressFormPresentationType-",
    ""
  ) as AddressFormPresentationType;
};

const hasVisibleAddressFormError = (
  addressFormType: ReactCheckout2022_AddressFormTypes,
  addressFormState?: ShippingAddressFormExpects | BillingAddressFormExpects
) => {
  if (!addressFormState) {
    return false;
  }

  const hasVisibleNameAndMobileError = () =>
    formTreeHasFrontendError(addressFormState.firstName) ||
    formTreeHasFrontendError(addressFormState.lastName) ||
    formTreeHasFrontendError(addressFormState.mobile);

  const renderedAddressFormPresentationType =
    getRenderedAddressFormPresentationType(addressFormType);
  switch (renderedAddressFormPresentationType) {
    case "LoggedInCustomerSavedAddresses":
      return formTreeHasFrontendError(
        (addressFormState as ShippingAddressFormExpects).savedAddress
      );
    case "Manual":
      return (
        hasVisibleNameAndMobileError() ||
        formTreeHasFrontendError(addressFormState.addressManual)
      );
    case "Autocomplete":
      return (
        hasVisibleNameAndMobileError() ||
        formTreeHasFrontendError(addressFormState.addressAutocomplete)
      );
    default:
      if (
        (addressFormState as ShippingAddressFormExpects).savedAddress?.enabled
      ) {
        return formTreeHasFrontendError(
          (addressFormState as ShippingAddressFormExpects).savedAddress
        );
      }
      if (
        addressFormState.addressAutocomplete?.currentValue ||
        addressFormState.addressAutocomplete
          ?.isValidatingAddressSelectionInProgress
      ) {
        return (
          hasVisibleNameAndMobileError() ||
          formTreeHasFrontendError(addressFormState.addressAutocomplete)
        );
      }
      return (
        hasVisibleNameAndMobileError() ||
        formTreeHasFrontendError(addressFormState.addressManual)
      );
  }
};

export const DeliveryDetailsUserExperienceProvider: React.FunctionComponent = (
  props
) => {
  const { handleBeginTheJoinDrawerUserExperience } =
    useJoinDrawerUserExperience();

  const { handleOpen: drawerDelivery_handleOpen } =
    useDrawerDelivery_asController();

  const { status: customerInformationQueryStatus } =
    useCustomerInformationQuery();

  const { hasInitialized: shippingClientStorageData_hasInitialized } =
    useDataMutableSourceShippingClientStorageData();
  const {
    handleLastSelectedSavedAddressIdChange:
      dataSourceMutableShippingClientStorageData_asController_handleLastSelectedSavedAddressIdChange,
    handleLastSelected_isBillingAndShippingTheSameChange:
      dataSourceMutableShippingClientStorageData_asController_handleLastSelected_isBillingAndShippingTheSameChange,
    handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange:
      dataSourceMutableShippingClientStorageData_asController_handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange,
  } = useDataMutableSourceShippingClientStorageData_asController();

  const { status: dataMutationDeleteCustomerAddress_status } =
    useDataMutationDeleteCustomerAddress();
  const { mutateAsync: dataMutationDeleteCustomerAddress_mutateAsync } =
    useDataMutationDeleteCustomerAddress_asController();

  const { mostLatestType: myBagDataResponseType } =
    useMyBagDataSourceIntersection();
  const { presentationMyBagOrder } = useMyBagPresentationModel();
  const { cartId } = useCustomerCartId();
  const { isCustomerLoggedIn } = useCustomerSession();

  const { initiateScrollAction } = usePresenterForScroll_asController();
  const { setProminentError } = useProminentError();

  const {
    isLoading: shippingPaymentPageKickoff_isLoading,
    hasBeenToThePaymentPage,
  } = useDeliveryDetailsPaymentPageKickoff();
  const { kickoffThePaymentPage, primeDeliveryDetailsSnapshot } =
    useDeliveryDetailsPaymentPageKickoff_asController();
  const { formRef: deliveryDetailsFormRef } = useDeliveryDetailsFormRef();
  const [deliveryDetailsFormState, deliveryDetailsFormLocate] =
    React44ms.React.useForm<DeliveryDetailsForm>(
      deliveryDetailsFormRef || undefined
    );
  const { lastFormattedAutocompletedAddress_forShipping } =
    useDataMutableSourceShippingSessionStorageData();
  const syncEmailFromDomIfNeeded = React.useCallback(() => {
    if (!deliveryDetailsFormRef) {
      return;
    }
    if (typeof document === "undefined") {
      return;
    }
    const formEmailValue =
      deliveryDetailsFormState?.shippingEmailConfirmation?.value;
    if (formEmailValue && formEmailValue.length > 0) {
      return;
    }
    const input = document.querySelector(
      ".email-confirmation input"
    ) as HTMLInputElement | null;
    const setEmailOnForm = (emailValue: string) => {
      React44ms.React.Event.dispatch(
        React44ms.React.Convenient.input.onChange,
        [
          deliveryDetailsFormRef,
          React44ms.Api.YieldablePort(
            React44ms.Api.Convenience.element.name<DeliveryDetailsForm>(
              "shippingEmailConfirmation"
            )
          ),
        ],
        emailValue
      );
    };
    if (input && input.value) {
      setEmailOnForm(input.value);
      return;
    }
    const cachedEmail = getLastKnownEmail();
    if (!cachedEmail || !input) {
      return;
    }
    input.value = cachedEmail;
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
    setEmailOnForm(cachedEmail);
  }, [deliveryDetailsFormRef, deliveryDetailsFormState]);

  const syncCachedShippingIntoForm = React.useCallback(() => {
    if (!deliveryDetailsFormLocate) {
      return false;
    }
    if (hasBeenToThePaymentPage) {
      return false;
    }
    const cachedShippingAddress = getLastKnownAddress("shipping");
    const cachedEmail = getLastKnownEmail();
    const hasCompleteCachedShipping =
      !!cachedEmail &&
      !!cachedShippingAddress.firstName &&
      !!cachedShippingAddress.lastName &&
      !!cachedShippingAddress.mobile &&
      !!cachedShippingAddress.addressLine1 &&
      !!cachedShippingAddress.suburb &&
      !!cachedShippingAddress.postcode &&
      !!cachedShippingAddress.state &&
      !!cachedShippingAddress.country;
    if (!hasCompleteCachedShipping) {
      return false;
    }
    let didSync = false;

    const setIfEmpty = (ref: any, currentValue: any, nextValue: any) => {
      if (!ref || !nextValue) {
        return;
      }
      if (currentValue && `${currentValue}`.length > 0) {
        return;
      }
      React44ms.React.Event.dispatch(
        React44ms.React.Convenient.input.onChange,
        ref,
        nextValue
      );
      didSync = true;
    };
    const setValue = (ref: any, nextValue: any) => {
      if (!ref || !nextValue) {
        return;
      }
      React44ms.React.Event.dispatch(
        React44ms.React.Convenient.input.onChange,
        ref,
        nextValue
      );
      didSync = true;
    };

    if (
      cachedEmail &&
      (deliveryDetailsFormState?.shippingEmailConfirmation?.error ||
        !deliveryDetailsFormState?.shippingEmailConfirmation?.value)
    ) {
      setValue(
        deliveryDetailsFormLocate.shippingEmailConfirmation,
        cachedEmail
      );
    }

    setIfEmpty(
      deliveryDetailsFormLocate.shipping?.firstName,
      deliveryDetailsFormState?.shipping?.firstName?.value,
      cachedShippingAddress.firstName
    );
    setIfEmpty(
      deliveryDetailsFormLocate.shipping?.lastName,
      deliveryDetailsFormState?.shipping?.lastName?.value,
      cachedShippingAddress.lastName
    );
    setIfEmpty(
      deliveryDetailsFormLocate.shipping?.mobile,
      deliveryDetailsFormState?.shipping?.mobile?.value,
      cachedShippingAddress.mobile
    );
    setIfEmpty(
      deliveryDetailsFormLocate.shipping?.addressManual?.addressLine1,
      deliveryDetailsFormState?.shipping?.addressManual?.addressLine1?.value,
      cachedShippingAddress.addressLine1
    );
    setIfEmpty(
      deliveryDetailsFormLocate.shipping?.addressManual?.addressLine2,
      deliveryDetailsFormState?.shipping?.addressManual?.addressLine2?.value,
      cachedShippingAddress.addressLine2
    );
    setIfEmpty(
      deliveryDetailsFormLocate.shipping?.addressManual?.suburb,
      deliveryDetailsFormState?.shipping?.addressManual?.suburb?.value,
      cachedShippingAddress.suburb
    );
    setIfEmpty(
      deliveryDetailsFormLocate.shipping?.addressManual?.postcode,
      deliveryDetailsFormState?.shipping?.addressManual?.postcode?.value,
      cachedShippingAddress.postcode
    );
    setIfEmpty(
      deliveryDetailsFormLocate.shipping?.addressManual?.state,
      deliveryDetailsFormState?.shipping?.addressManual?.state?.value,
      cachedShippingAddress.state
    );
    setIfEmpty(
      deliveryDetailsFormLocate.shipping?.addressManual?.country,
      deliveryDetailsFormState?.shipping?.addressManual?.country?.value,
      cachedShippingAddress.country
    );

    const shouldSeedAutocomplete =
      !deliveryDetailsFormState?.shipping?.addressAutocomplete
        ?.hasBeenSelectedFromTheAutocomplete ||
      !deliveryDetailsFormState?.shipping?.addressAutocomplete?.currentValue;
    if (shouldSeedAutocomplete && lastFormattedAutocompletedAddress_forShipping) {
      React44ms.React.Event.dispatch(
        React44ms.React.Convenient.input.onChange,
        [
          deliveryDetailsFormLocate.shipping?.addressAutocomplete,
          React44ms.Api.YieldablePort(
            React44ms.Api.Convenience.element.name<any>("handleSelectASuggestion")
          ),
        ],
        lastFormattedAutocompletedAddress_forShipping
      );
      didSync = true;
    }

    return didSync;
  }, [
    deliveryDetailsFormLocate,
    hasBeenToThePaymentPage,
    deliveryDetailsFormState?.shipping?.addressAutocomplete?.currentValue,
    deliveryDetailsFormState?.shipping?.addressAutocomplete
      ?.hasBeenSelectedFromTheAutocomplete,
    deliveryDetailsFormState?.shipping?.addressManual?.addressLine1?.value,
    deliveryDetailsFormState?.shipping?.addressManual?.addressLine2?.value,
    deliveryDetailsFormState?.shipping?.addressManual?.country?.value,
    deliveryDetailsFormState?.shipping?.addressManual?.postcode?.value,
    deliveryDetailsFormState?.shipping?.addressManual?.state?.value,
    deliveryDetailsFormState?.shipping?.addressManual?.suburb?.value,
    deliveryDetailsFormState?.shipping?.firstName?.value,
    deliveryDetailsFormState?.shipping?.lastName?.value,
    deliveryDetailsFormState?.shipping?.mobile?.value,
    deliveryDetailsFormState?.shippingEmailConfirmation?.error,
    deliveryDetailsFormState?.shippingEmailConfirmation?.value,
    lastFormattedAutocompletedAddress_forShipping,
  ]);

  const deliveryFormPresentable: DeliveryDetailsUserExperienceConsumerInterface["deliveryFormPresentable"] =
    React.useMemo(() => {
      const customerInformationQueryStatus_indicatesItHasResolved =
        customerInformationQueryStatus === "success" ||
        customerInformationQueryStatus === "error";
      const weHaveABag =
        myBagDataResponseType === "data" ||
        myBagDataResponseType === "error" ||
        (!cartId && !isCustomerLoggedIn);
      return (
        customerInformationQueryStatus_indicatesItHasResolved &&
        shippingClientStorageData_hasInitialized &&
        weHaveABag
      );
    }, [
      customerInformationQueryStatus,
      shippingClientStorageData_hasInitialized,
      myBagDataResponseType,
      cartId,
      isCustomerLoggedIn,
    ]);

  const isLoading: DeliveryDetailsUserExperienceConsumerInterface["isLoading"] =
    React.useMemo(() => {
      return (
        shippingPaymentPageKickoff_isLoading ||
        dataMutationDeleteCustomerAddress_status === "loading"
      );
    }, [
      shippingPaymentPageKickoff_isLoading,
      dataMutationDeleteCustomerAddress_status,
    ]);

  const handleUserWantsToLoginInsteadBecauseTheirEmailIsAlreadyRegistered: DeliveryDetailsUserExperience_ControllerInterface["handleUserWantsToLoginInsteadBecauseTheirEmailIsAlreadyRegistered"] =
    React.useCallback(() => {
      handleBeginTheJoinDrawerUserExperience();
    }, [handleBeginTheJoinDrawerUserExperience]);

  const handleShowDrawerDelivery_viewItemDetailsLink: DeliveryDetailsUserExperience_ControllerInterface["handleShowDrawerDelivery_viewItemDetailsLink"] =
    React.useCallback(() => {
      drawerDelivery_handleOpen();
    }, [drawerDelivery_handleOpen]);

  const handleLastSelectedSavedAddressIdChange: DeliveryDetailsUserExperience_ControllerInterface["handleLastSelectedSavedAddressIdChange"] =
    React.useCallback(
      (lastSelectedSavedAddressId) => {
        dataSourceMutableShippingClientStorageData_asController_handleLastSelectedSavedAddressIdChange(
          lastSelectedSavedAddressId
        );
      },
      [
        dataSourceMutableShippingClientStorageData_asController_handleLastSelectedSavedAddressIdChange,
      ]
    );

  const handleDeleteSavedAddress: DeliveryDetailsUserExperience_ControllerInterface["handleDeleteSavedAddress"] =
    React.useCallback(
      async (savedAddress) => {
        const request: DataMutationDeleteCustomerAddress_Request = {
          addressId: savedAddress.id,
        };
        try {
          await dataMutationDeleteCustomerAddress_mutateAsync(request);
        } catch (anyError) {
          console.error(anyError);
          if (anyError && (anyError as Error).message) {
            setProminentError(
              (anyError as Error).message,
              DeliveryDetailsUserExperience_ProminentErrorAreas.DELETE_ADDRESS
            );
          }
        }
      },
      [dataMutationDeleteCustomerAddress_mutateAsync, setProminentError]
    );

  const handleLastSelected_isBillingAndShippingTheSameChange: DeliveryDetailsUserExperience_ControllerInterface["handleLastSelected_isBillingAndShippingTheSameChange"] =
    React.useCallback(
      (lastSelected_isBillingAndShippingTheSame) => {
        dataSourceMutableShippingClientStorageData_asController_handleLastSelected_isBillingAndShippingTheSameChange(
          lastSelected_isBillingAndShippingTheSame
        );
      },
      [
        dataSourceMutableShippingClientStorageData_asController_handleLastSelected_isBillingAndShippingTheSameChange,
      ]
    );

  const handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange: DeliveryDetailsUserExperience_ControllerInterface["handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange"] =
    React.useCallback(
      async (lastSelected_authoriseDeliveryToBeLeftWithoutSignature) => {
        await dataSourceMutableShippingClientStorageData_asController_handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange(
          lastSelected_authoriseDeliveryToBeLeftWithoutSignature
        );
      },
      [
        dataSourceMutableShippingClientStorageData_asController_handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange,
      ]
    );

  const handleReviewAndPay: DeliveryDetailsUserExperience_ControllerInterface["handleReviewAndPay"] =
    React.useCallback(async () => {
      syncEmailFromDomIfNeeded();
      const getFormValidationResult = () =>
        React44ms.React.Convenient.form.onSubmit(
          React44ms.Api.Convenience.RefPrimitive.on<ReactCheckout2022_AllTheForms>(
            "deliveryDetails"
          ),
          {} as unknown as React.FormEvent<HTMLFormElement>
        );
      const hasAnyVisibleFrontendFormError = () => {
        const shouldValidateBillingErrors =
          !!presentationMyBagOrder?.isThisAVirtualOrder ||
          !deliveryDetailsFormState?.isBillingAndShippingTheSame?.value;
        return (
          formTreeHasFrontendError(
            deliveryDetailsFormState?.shippingEmailConfirmation
          ) ||
          hasVisibleAddressFormError(
            ReactCheckout2022_AddressFormTypes.shipping,
            deliveryDetailsFormState?.shipping
          ) ||
          (shouldValidateBillingErrors &&
            hasVisibleAddressFormError(
              ReactCheckout2022_AddressFormTypes.billing,
              deliveryDetailsFormState?.billing
            ))
        );
      };
      const blockNavigationWithFormError = () => {
        setProminentError(
          "Some information is missing or invalid. Check the red fields above for details."
        );
        initiateScrollAction(
          PRESENTATION_SCROLL_ACTIONS.SCROLL_TO_FORM_ERROR_FIELD
        );
      };
      let { reasons_this_form_is_invalid } = getFormValidationResult();
      if (reasons_this_form_is_invalid) {
        const didSyncFromCache = syncCachedShippingIntoForm();
        if (didSyncFromCache) {
          await Promise.resolve();
          const retryValidation = getFormValidationResult();
          reasons_this_form_is_invalid =
            retryValidation.reasons_this_form_is_invalid;
          if (!reasons_this_form_is_invalid) {
            if (hasAnyVisibleFrontendFormError()) {
              blockNavigationWithFormError();
              return;
            }
            primeDeliveryDetailsSnapshot(deliveryDetailsFormState || null);
            await kickoffThePaymentPage();
            return;
          }
        }
        const hasAutocompleteShippingAddress =
          !!deliveryDetailsFormState?.shipping?.addressAutocomplete
            ?.currentValue;
        const isShippingAddressParsingInProgress =
          !!deliveryDetailsFormState?.shipping?.addressAutocomplete
            ?.isValidatingAddressSelectionInProgress;
        const hasAnyFrontendFormError = hasAnyVisibleFrontendFormError();
        if (
          !hasAnyFrontendFormError &&
          (hasAutocompleteShippingAddress ||
            isShippingAddressParsingInProgress)
        ) {
          await Promise.resolve();
          primeDeliveryDetailsSnapshot(deliveryDetailsFormState || null);
          await kickoffThePaymentPage();
          return;
        }
        if (
          reasons_this_form_is_invalid[
            React44ms.Api.submit.NO_SUBMIT_FOUND_INVALID_FORM_REASON_KEY
          ]
        ) {
          console.error(
            "React44ms.Api.submit.NO_SUBMIT_FOUND_INVALID_FORM_REASON_KEY"
          );
        }

        for (const [source, message] of Object.entries(
          reasons_this_form_is_invalid
        )) {
          console.error(source, message);
        }

        blockNavigationWithFormError();
        return;
      }

      if (hasAnyVisibleFrontendFormError()) {
        blockNavigationWithFormError();
        return;
      }

      await Promise.resolve();
      primeDeliveryDetailsSnapshot(deliveryDetailsFormState || null);

      await kickoffThePaymentPage();
    }, [
      kickoffThePaymentPage,
      deliveryDetailsFormState,
      syncCachedShippingIntoForm,
      primeDeliveryDetailsSnapshot,
      syncEmailFromDomIfNeeded,
      presentationMyBagOrder?.isThisAVirtualOrder,
      setProminentError,
      initiateScrollAction,
    ]);

  const value: DeliveryDetailsUserExperienceConsumerInterface = React.useMemo(
    () => ({
      deliveryFormPresentable,
      isLoading,
    }),
    [deliveryFormPresentable, isLoading]
  );
  const controllerValue: DeliveryDetailsUserExperience_ControllerInterface =
    React.useMemo(
      () => ({
        handleUserWantsToLoginInsteadBecauseTheirEmailIsAlreadyRegistered,
        handleShowDrawerDelivery_viewItemDetailsLink,
        handleLastSelectedSavedAddressIdChange,
        handleDeleteSavedAddress,
        handleLastSelected_isBillingAndShippingTheSameChange,
        handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange,
        handleReviewAndPay,
      }),
      [
        handleUserWantsToLoginInsteadBecauseTheirEmailIsAlreadyRegistered,
        handleShowDrawerDelivery_viewItemDetailsLink,
        handleLastSelectedSavedAddressIdChange,
        handleDeleteSavedAddress,
        handleLastSelected_isBillingAndShippingTheSameChange,
        handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange,
        handleReviewAndPay,
      ]
    );
  return (
    <DeliveryDetailsUserExperienceContext.Provider value={value}>
      <DeliveryDetailsUserExperience_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children}
      </DeliveryDetailsUserExperience_ControllerContext.Provider>
    </DeliveryDetailsUserExperienceContext.Provider>
  );
};

export const useDeliveryDetailsUserExperience = () => {
  const context = React.useContext(DeliveryDetailsUserExperienceContext);
  if (context === defaultValue) {
    throw new Error(
      "useDeliveryDetailsUserExperience must be used within a DeliveryDetailsUserExperienceProvider"
    );
  }
  return context;
};

export const useDeliveryDetailsUserExperience_asController = () => {
  const context = React.useContext(
    DeliveryDetailsUserExperience_ControllerContext
  );
  if (context === controllerDefaultValue) {
    throw new Error(
      "useDeliveryDetailsUserExperience_asController must be used within a DeliveryDetailsUserExperienceProvider"
    );
  }
  return context;
};
